import * as React from 'react'
import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Spinner, Tabs } from '@factorialco/f0-react/dist/experimental'
import {
  FindWorkflowDocument,
  NewRequestPageDocument,
  RequestTypeDetailDocument,
  RequestTypeDetailQuery,
  WorkflowGraphFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { formFromData } from 'modules/forms/hooks/useForms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RequestTypeDetailHeader } from 'modules/procurement/components/RequestTypeDetailHeader'
// Import modal components
import PublishTypeModal from 'modules/procurement/components/procurementTypes/PublishTypeModal'
import VersioningRequestTypeModal from 'modules/procurement/components/procurementTypes/VersioningRequestTypeModal'
import { NewPurchaseRequestModal } from 'modules/procurement/components/purchaseRequests/NewRequestModal'
import { WorkflowProvider, useWorkflow } from 'modules/workflows/contexts/WorkflowProvider'

const FormTab = React.lazy(() => import('modules/procurement/components/procurementTypes/Form'))
const WorkflowsTab = React.lazy(
  () => import('modules/procurement/components/procurementTypes/Workflows')
)

function RequestTypeDetailPageContent() {
  const { id } = useParams<{ id: string }>()
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = searchParams.get('tab') || 'form'

  // Consume workflow state from WorkflowProvider
  const { isDirty: workflowIsDirty, saveWorkflow } = useWorkflow()

  const LoadingSpinner = () => (
    <Box height="full" alignItems="center" justifyContent="center">
      <Spinner />
    </Box>
  )

  // Modal states
  const [publishTypeModalOpen, setPublishTypeModalOpen] = useState(false)
  const [versioningFormModalOpen, setVersioningFormModalOpen] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  // Workflow state
  const [currentWorkflow, setCurrentWorkflow] = useState<WorkflowGraphFragment | null>(null)

  // Modal handlers
  const handlePublish = () => {
    setPublishTypeModalOpen(true)
  }

  const handleVersioningForm = () => {
    setVersioningFormModalOpen(true)
  }

  const handlePreview = () => {
    setShowPreviewModal(true)
  }

  const { data, loading, error } = useQuery<
    RequestTypeDetailQuery,
    { requestTypeId: number; companyId: number }
  >(RequestTypeDetailDocument, {
    variables: {
      requestTypeId: Number(id),
      companyId: company?.id || 0,
    },
    skip: !id || !company?.id,
  })

  // Fetch data needed for the NewPurchaseRequestModal
  const { data: modalData } = useQuery(NewRequestPageDocument, {
    variables: { companyId: company?.id || 0, enabled: true },
    skip: !company?.id || !showPreviewModal,
  })

  const currencies = modalData?.apiCore?.currenciesConnection?.edges?.map((edge) => edge.node) || []
  const userCurrency =
    modalData?.apiCore?.currentsConnection?.nodes?.[0]?.employee?.legalEntity?.currency || ''

  const requestType = data?.procurement?.typesConnection?.nodes?.[0]
  const isCustomFormsEnabled =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_procurement_custom_forms'
    )?.enabled ?? false

  const form = requestType?.formsConnection?.nodes?.[0]
    ? formFromData({
        ...requestType.formsConnection.nodes[0],
        description: requestType.formsConnection.nodes[0].description ?? undefined,
        resourceId: requestType.formsConnection.nodes[0].resourceId ?? undefined,
        elements: requestType.formsConnection.nodes[0].elements.map((element) => ({
          ...element,
          description: element.description ?? '',
        })),
      })
    : undefined

  const isWorkflowsEnabled =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_procurement_approval_workflows'
    )?.enabled ?? false

  // Query workflows at the page level so it's always active
  const { data: workflowsData } = useQuery(FindWorkflowDocument, {
    variables: {
      companyId: company?.id || 0,
      useCase: 'approvals_purchase_request_approval_flow',
      targetType: 'Procurement::Type',
      targetIds: [Number(id)],
    },
    skip: !company?.id || !id || !isWorkflowsEnabled,
  })

  // Extract the workflow from the query
  const workflows = workflowsData?.workflows?.workflowsConnection?.nodes || []

  // TODO the logic here is temporal while the editor only handles one worflow and not all the ones that can
  // possibly exist. So we pick "the active one or the last one".
  const workflow =
    workflows.find((x) => x.status === 'active') ||
    workflows.sort((a, b) => b.version - a.version)[0] ||
    null

  // Update currentWorkflow state whenever workflow data changes
  React.useEffect(() => {
    setCurrentWorkflow(workflow)
  }, [workflow])

  const renderTabContent = () => {
    if (!requestType) return null

    switch (activeTab) {
      case 'workflows':
        if (!isWorkflowsEnabled) {
          // If workflows is disabled, redirect to form tab
          setSearchParams({ tab: 'form' })
          return null
        }
        return (
          <WorkflowsTab
            requestType={requestType}
            workflow={currentWorkflow}
            onWorkflowCreated={setCurrentWorkflow}
          />
        )
      case 'form':
      default:
        return (
          <FormTab
            requestType={requestType}
            form={form}
            isCustomFormsEnabled={isCustomFormsEnabled}
            onPreviewClick={handlePreview}
          />
        )
    }
  }

  return (
    <>
      <ResponsivePage.Body
        loading={loading}
        error={error}
        variant="fullscreen"
        authorized={isCustomFormsEnabled}
        showHeader={false}
      >
        {requestType && (
          <RequestTypeDetailHeader
            form={form}
            onPreview={handlePreview}
            onPublish={handlePublish}
            onVersioning={handleVersioningForm}
            onSaveWorkflow={saveWorkflow}
            workflow={currentWorkflow}
            isWorkflowsEnabled={isWorkflowsEnabled}
            workflowIsDirty={workflowIsDirty}
            requestType={requestType}
          />
        )}
        <Tabs
          key={activeTab}
          activeTabId={activeTab}
          tabs={[
            {
              id: 'form',
              href: `${navigation.settings.procurement.requestType.id.fullPath({
                id: id || '',
              })}?tab=form`,
              label: i18n.t('procurement.purchase_requests.settings.types.detail.tabs.form.title'),
              index: true,
            },
            isWorkflowsEnabled && {
              id: 'workflows',
              href: `${navigation.settings.procurement.requestType.id.fullPath({
                id: id || '',
              })}?tab=workflows`,
              label: i18n.t(
                'procurement.purchase_requests.settings.types.detail.tabs.workflows.title'
              ),
            },
          ].filter(Boolean)}
        />

        <React.Suspense fallback={<LoadingSpinner />}>{renderTabContent()}</React.Suspense>

        {/* Modals */}
        {publishTypeModalOpen && requestType && (
          <PublishTypeModal
            procurementType={requestType}
            workflow={currentWorkflow}
            onClose={() => setPublishTypeModalOpen(false)}
          />
        )}

        {versioningFormModalOpen && requestType && form && (
          <VersioningRequestTypeModal
            procurementType={requestType}
            form={form}
            onClose={() => setVersioningFormModalOpen(false)}
          />
        )}

        {showPreviewModal && requestType && (
          <NewPurchaseRequestModal
            procurementType={requestType}
            currencies={currencies}
            defaultCurrency={userCurrency}
            hasCustomFormsEnabled={isCustomFormsEnabled}
            onClose={() => setShowPreviewModal(false)}
            isPreviewMode
          />
        )}
      </ResponsivePage.Body>
    </>
  )
}

// Container component that provides WorkflowProvider context
const RequestTypeDetailPage = () => {
  return (
    <WorkflowProvider>
      <RequestTypeDetailPageContent />
    </WorkflowProvider>
  )
}

export default RequestTypeDetailPage
